import { cn } from '@poseidon/components/cn';
import { Button, Tooltip } from '@poseidon/components/Input2';
import { General, Time } from '@poseidon/components/StrokeIcons';

import { STEP_META, stepIssueRingClass, stepLabel, type SequenceStep, type StepIssue } from '~/components/Sequence/sequenceSteps';
import type { SequenceStepVisual } from '~/components/Sequence/sequenceStepStatus';
import { SequenceAwaitNode } from '~/components/Sequence/SequenceAwaitNode';
import { SequenceStepIssueTooltip } from '~/components/Sequence/SequenceStepIssueTooltip';
import { SequenceWaitNode } from '~/components/Sequence/SequenceWaitNode';
import { type WaitUnit } from '~/components/Sequence/WaitDurationInput';
import { useGraphRender } from '~/components/Sequence/sequenceDebug';

interface Props {
  step: SequenceStep;
  dragging: boolean;
  /** A validation problem with this step (e.g. "Await accept" before any Connect), or null. Errors ring red,
   *  advisory warnings ring amber. */
  invalid: StepIssue | null;
  /** Remove this step. Omit (or `readOnly`) → no remove ✕. */
  onRemove?: () => void;
  /** Commit a new wait length (canonical days) + display unit for a `wait` step. Omit (or `readOnly`) → static. */
  onChangeWait?: (days: number, unit: WaitUnit) => void;
  /** Commit a new acceptance deadline for an `await_connect` step. Omit (or `readOnly`) → static. */
  onChangeDeadlineDays?: (days: number) => void;
  /**
   * Locked view (the per-contact contact-detail graph): freeze structure — no remove ✕, and the
   * `wait` / `await_connect` durations render as STATIC pills (a per-contact override can't change delays).
   */
  readOnly?: boolean;
  /**
   * Locked view: what became of this step for the open contact ({@link sequenceStepVisual}) — done, skipped,
   * never-taken, or where they're resting right now. Omit (the builder) → the neutral authoring style.
   */
  visual?: SequenceStepVisual | null;
}

/**
 * A single step in the sequence graph — an action step is a small rounded icon tile; a wait step is
 * a rounded-full pill with its editable duration. Clicking the node opens its content editor; dragging
 * it (past a small threshold — see {@link SequenceGraphEditor}) reorders it. Delete slides in on hover.
 * An invalid step gets a red ring + a reason tooltip.
 */
export function SequenceGraphNode({
  step,
  dragging,
  invalid,
  onRemove,
  onChangeWait,
  onChangeDeadlineDays,
  readOnly = false,
  visual,
}: Props) {
  useGraphRender('SequenceGraphNode', () => ({ type: step.type }));
  // wait / await_connect render as editable "N days" pills; every other step is a plain icon tile.
  const isDurationStep = step.type === 'wait' || step.type === 'await_connect';
  const chipBase = cn(
    !readOnly && 'cursor-grab',
    'shadow-sm transition-shadow hover:shadow-md',
    dragging && 'cursor-grabbing shadow-lg ring-2 ring-blue-400',
    !dragging && stepIssueRingClass(invalid),
    // Per-contact state LAST so it wins the border/fill over the authoring defaults (cn is tailwind-merge).
    isDurationStep ? visual?.chipWide : visual?.chip,
  );

  return (
    <div className="group/node relative inline-flex items-center">
      {!isDurationStep && (
        <Tooltip
          as="span"
          tooltip={
            invalid != null ? (
              <SequenceStepIssueTooltip issue={invalid} />
            ) : visual != null ? (
              `${stepLabel(step)} — ${visual.tooltip}`
            ) : (
              stepLabel(step)
            )
          }
          className={cn(
            'relative flex size-9 items-center justify-center rounded-lg border border-lynch-200 bg-white text-lynch-600 dark:border-slate-700 dark:bg-slate-900 dark:text-lynch-300 [&>svg]:size-5',
            chipBase,
          )}>
          {STEP_META[step.type].icon}
          {/* A step that ran gets a corner badge rather than the old fade — "done" should read as something
              that happened, not as something switched off — and the badge says HOW it landed: a connection
              made, an invitation still hanging, or one we took back. */}
          {visual?.medallion != null && (
            <span
              className={cn(
                'absolute -bottom-1 -right-1 flex size-3.5 items-center justify-center rounded-full text-white ring-2 ring-slate-50 dark:ring-neutral-900 [&>svg]:size-2.5',
                visual.medallion === 'check' && 'bg-mantis-500',
                visual.medallion === 'pending' && 'bg-amber-400',
                visual.medallion === 'withdrawn' && 'bg-lynch-400',
              )}>
              {visual.medallion === 'check' && <General.Check />}
              {visual.medallion === 'pending' && <Time.Clock />}
              {visual.medallion === 'withdrawn' && <General.XMark />}
            </span>
          )}
        </Tooltip>
      )}
      {/* A wait renders as a compact static pill; the editable duration opens in a popover on click. */}
      {step.type === 'wait' && (
        <SequenceWaitNode step={step} chipBase={chipBase} onChangeWait={readOnly ? undefined : onChangeWait} />
      )}
      {step.type === 'await_connect' && (
        <SequenceAwaitNode
          step={step}
          chipBase={chipBase}
          invalid={invalid}
          onChangeDeadlineDays={readOnly ? undefined : onChangeDeadlineDays}
        />
      )}

      {/* Corner ✕ (matches the parallel-chip delete), confirm-first so a stray click can't drop a step.
          Hidden in the locked (per-contact) view — structure can't change there. */}
      {!readOnly && onRemove != null && (
        <Button
          size="2xs"
          color="red"
          tone="soft"
          aria-label="Remove step"
          tooltip="Remove step"
          icon={<General.XMark />}
          onClick={onRemove}
          confirm={{ title: 'Remove this step?', confirmLabel: 'Remove', position: 'top span-left' }}
          className={cn(
            'absolute -right-1.5 -top-1.5',
            'pointer-events-none opacity-0 transition-opacity group-hover/node:pointer-events-auto group-hover/node:opacity-100',
          )}
        />
      )}
    </div>
  );
}
